import React, { useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import "./DashboardContent.css";
import Dashboardmilestonesection from "./dashboardmilestonesection";
import Dashboardbadgessection from "./dashboardbadgessection";
import Dashboardpointssection from "./dashboardpointssection";
import { getLearnerCourses } from "Redux/CourseDetails";
import { getCategoryPoints, fetchFoundationStatus } from "Redux/LearnersReport";
import { getUserData } from "Redux/UserDetails";
import { getCoursesGroupedByCategoryAndModule } from "utils/foundationUnlock";
import AOS from "aos";
import "aos/dist/aos.css";
import { dashboardAssets } from "../../imagesData/ImagesData";
import useFetchImageUrls from "../../ConstantUseFetchImageUrls";

const computeHubProgress = ({ hub, userData, userLevel }) => {
  if (!hub?.courses?.length || !userData) return 0;

  const groupedCourses = getCoursesGroupedByCategoryAndModule({
    coursesByHub: [hub],
    userData,
    userLevel,
    hubName: hub.hubName,
  });

  let totalMandatory = 0;
  let completedMandatory = 0;

  Object.values(groupedCourses).forEach((category) => {
    Object.values(category).forEach((moduleObj) => {
      const mandatoryCourses = moduleObj.Mandatory || [];

      totalMandatory += mandatoryCourses.length;
      completedMandatory += mandatoryCourses.filter((c) =>
        Boolean(c.completionDate),
      ).length;
    });
  });

  if (totalMandatory === 0) return 0;

  return completedMandatory / totalMandatory;
};

function DashboardContent() {
  const query = useMemo(
    () => `params=${encodeURIComponent(JSON.stringify(dashboardAssets))}`,
    [],
  );
  const { imageUrls, error } = useFetchImageUrls(query);

  const urls = imageUrls?.imageUrl || {};

  const { instance } = useMsal();
  const dispatch = useDispatch();
  const activeAccount = instance.getActiveAccount();
  const enterpriseId = activeAccount?.idTokenClaims?.enterpriseID;
  const userLevelDes = activeAccount?.idTokenClaims?.jobFamilyDescription;

  const { coursesByHub } = useSelector((state) => state.courseDetails);
  const userData = useSelector((state) => state.userDetails.userData?.[0]);
  const { categoryPoints } = useSelector((state) => state.learnersReport);

  const isEmpty = (value) =>
    value === null || value === undefined || value === "" || value === "NULL";
  const userLevel = isEmpty(userLevelDes) ? userData?.level : userLevelDes;

  const { completedCourses, badgesEarned, totalPoints, averageHubProgress } =
    useMemo(() => {
      if (!coursesByHub || !userData) {
        return {
          completedCourses: [],
          badgesEarned: 0,
          totalPoints: 0,
          averageHubProgress: 0,
        };
      }

      let completedCourses = [];
      let badgesEarned = 0;
      let totalPoints = 0;
      let hubProgressSum = 0;

      coursesByHub.forEach((hub) => {
        const hubProgress = computeHubProgress({
          hub,
          userData,
          userLevel,
        });

        hubProgressSum += hubProgress;

        const groupedCourses = getCoursesGroupedByCategoryAndModule({
          coursesByHub: [hub],
          userData,
          userLevel,
          hubName: hub.hubName,
        });

        Object.values(groupedCourses).forEach((category) => {
          Object.values(category).forEach((moduleObj) => {
            const mandatoryCourses = moduleObj.Mandatory || [];
            const optionalCourses = moduleObj.Optional || [];
            const allCoursesInModule = [
              ...mandatoryCourses,
              ...optionalCourses,
            ];

            const completedInModule = allCoursesInModule.filter((c) =>
              Boolean(c.completionDate),
            );
            completedCourses.push(...completedInModule);

            totalPoints += completedInModule.reduce(
              (acc, c) => acc + (c.coursePoints || 0),
              0,
            );

            if (
              mandatoryCourses.length &&
              mandatoryCourses.every((c) => Boolean(c.completionDate))
            ) {
              badgesEarned += 1;
            }
          });
        });
      });

      const averageHubProgress =
        coursesByHub.length > 0
          ? Math.round((hubProgressSum / coursesByHub.length) * 100)
          : 0;

      return {
        completedCourses,
        badgesEarned,
        totalPoints,
        averageHubProgress,
      };
    }, [coursesByHub, userData, userLevel]);

  const completedCount = completedCourses.length;

  const earnedBadges = useMemo(() => {
    const badgeCounts = {
      bronze: 0,
      silver: 0,
      gold: 0,
      diamond: 0,
      platinum: 0,
    };

    if (!coursesByHub || !userData) {
      return {
        bronze: { earned: false, count: 0 },
        silver: { earned: false, count: 0 },
        gold: { earned: false, count: 0 },
        diamond: { earned: false, count: 0 },
        platinum: { earned: false, count: 0 },
      };
    }

    coursesByHub.forEach((hub) => {
      const groupedCourses = getCoursesGroupedByCategoryAndModule({
        coursesByHub: [hub],
        userData,
        userLevel,
        hubName: hub.hubName,
      });

      Object.values(groupedCourses).forEach((category) => {
        Object.values(category).forEach((moduleObj) => {
          const mandatoryCourses = moduleObj.Mandatory || [];

          if (
            mandatoryCourses.length &&
            mandatoryCourses.every((c) => Boolean(c.completionDate))
          ) {
            const completedBadgeCourse = mandatoryCourses.find(
              (course) => course.badge,
            );

            const badge = completedBadgeCourse?.badge?.toLowerCase();

            if (
              badge &&
              Object.prototype.hasOwnProperty.call(badgeCounts, badge)
            ) {
              badgeCounts[badge] += 1;
            }
          }
        });
      });
    });

    return {
      bronze: {
        earned: badgeCounts.bronze > 0,
        count: badgeCounts.bronze,
      },
      silver: {
        earned: badgeCounts.silver > 0,
        count: badgeCounts.silver,
      },
      gold: {
        earned: badgeCounts.gold > 0,
        count: badgeCounts.gold,
      },
      diamond: {
        earned: badgeCounts.diamond > 0,
        count: badgeCounts.diamond,
      },
      platinum: {
        earned: badgeCounts.platinum > 0,
        count: badgeCounts.platinum,
      },
    };
  }, [coursesByHub, userData, userLevel]);

  useEffect(() => {
    const fetchData = async () => {
      if (!enterpriseId) return;

      try {
        await Promise.all([
          dispatch(getLearnerCourses(enterpriseId, userData?.businessUnit)),
          dispatch(getCategoryPoints(enterpriseId, "ALL")),
          dispatch(fetchFoundationStatus(enterpriseId)),
          dispatch(getUserData(enterpriseId)),
        ]);
      } catch (err) {
        console.error("Dashboard data fetch error:", err);
      }
    };

    fetchData();
  }, [enterpriseId, dispatch, userData?.businessUnit]);

  useEffect(() => {
    const handleAOS = () => {
      if (window.innerWidth > 768) {
        AOS.init({ duration: 800 });
      } else {
        AOS.refreshHard();
        document.querySelectorAll("[data-aos]").forEach((el) => {
          el.removeAttribute("data-aos");
        });
      }
    };

    handleAOS();
    window.addEventListener("resize", handleAOS);

    return () => window.removeEventListener("resize", handleAOS);
  }, []);

  if (error) return <div>Error loading assets.</div>;
  if (!imageUrls || Object.keys(imageUrls).length === 0) return null;

  return (
    <>
      <div className="few-pages-banner-wrap">
        <img
          src={urls["bannerDashboard"]}
          alt="Ignite banner"
          className="banner-img"
        />
        <div className="few-pages-banner-box">
          <div className="few-pages-banner-content">
            <h1 className="banner-main-head">Your Learning. Your Progress</h1>
            <p className="banner-sub-text">
              A personalized view of your learning journey, track progress,
              achievements, and milestones.
            </p>
          </div>
        </div>
      </div>

      <div className="progress-glance-section">
        <h2 className="section-headings">Learning Progress at a Glance</h2>
        <p className="section-sub-text">
          A unified view of learning activity across Learning Hubs, milestones,
          points, and badges bringing structure and visibility to your
          individual learning journey.
        </p>
        <div className="section-sub-text-wrap">
          <p className="section-sub-text">
            Note: Completed a course in Workday? Your Ignite dashboard will be
            updated during the weekly refresh every Wednesday.
          </p>
        </div>

        <div className="progress-glance-wrap">
          <div className="progress-glance-row row1">
            <div className="progress-glance-box">
              <h3 className="h3-head">Your Progress</h3>

              <div className="progress-bar-box">
                <div className="progress-container">
                  <div className="progress-header">
                    <span className="progress-label">
                      Overall progress across Learning Hubs
                    </span>
                    <span className="progress-value">
                      {averageHubProgress}%
                    </span>
                  </div>
                  <div className="progress-track">
                    <div
                      className="progress-fill"
                      style={{ width: `${averageHubProgress}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="dashboard-course-point-badge-wrap">
                <div
                  className="dashboard-course-point-badge-box"
                  data-aos="fade-left"
                >
                  <div className="dashboard-courses-box">
                    <img
                      src={urls["coursesIcon"]}
                      alt="icon"
                      className="dashboard-all-icon"
                    />
                  </div>
                  <h3>
                    Courses completed <br /> <span>{completedCount}</span>
                  </h3>
                </div>

                <div
                  className="dashboard-course-point-badge-box"
                  data-aos="zoom-in"
                >
                  <div className="dashboard-points-box">
                    <img
                      src={urls["pointsIcon"]}
                      alt="icon"
                      className="dashboard-all-icon"
                    />
                  </div>
                  <h3>
                    Points Earned <br /> <span>{totalPoints}</span>
                  </h3>
                </div>

                <div
                  className="dashboard-course-point-badge-box"
                  data-aos="fade-right"
                >
                  <div className="dashboard-badges-box">
                    <img
                      src={urls["badgesIcon"]}
                      alt="icon"
                      className="dashboard-all-icon"
                    />
                  </div>
                  <h3>
                    Badges Earned <br /> <span>{badgesEarned}</span>
                  </h3>
                </div>
              </div>

              <div className="dashboard-onestep-wrap">
                <div
                  className="dashboard-onestep-row row1"
                  data-aos="fade-left"
                />
                <div
                  className="dashboard-onestep-row row2"
                  data-aos="fade-right"
                />
              </div>

              <div className="dashboard-milestone-sec">
                <h3 className="h3-head">Milestone Progress</h3>
                <p className="h3-below-text">Track your achievements</p>
                <Dashboardmilestonesection />
              </div>
            </div>
          </div>

          <div className="progress-glance-row row2">
            <div className="progress-glance-box">
              <div className="dashboard-badges-sec">
                <Dashboardbadgessection earnedBadges={earnedBadges} />
              </div>
              <div className="dashboard-points-sec">
                <Dashboardpointssection />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DashboardContent;
